import moment, { Moment } from 'moment';
import { computed, markRaw, ref } from 'vue';
import { Nullish } from '@/shared/utils/typescript';

class TimeService {
  private readonly seconds = ref(0);
  readonly now = computed(() => markRaw(moment(this.seconds.value * 1000)));

  constructor() {
    setInterval(this.updateSeconds.bind(this), 100);
  }

  private updateSeconds(): void {
    this.seconds.value = Math.floor(Date.now() / 1000);
  }

  private clone(): Moment {
    return this.now.value.clone();
  }

  today(): Moment {
    return this.clone().startOf('day');
  }

  yesterday(): Moment {
    return this.clone().subtract(1, 'day').startOf('day');
  }

  tomorrow(): Moment {
    return this.clone().add(1, 'day').startOf('day');
  }

  isYesterday(date: Nullish<Moment>): boolean {
    return date?.isSame(this.yesterday(), 'day') ?? false;
  }

  isToday(date: Nullish<Moment>): boolean {
    return date?.isSame(this.today(), 'day') ?? false;
  }

  isPast(date: Nullish<Moment>, granularity: moment.unitOfTime.StartOf = 'day'): boolean {
    return date?.isBefore(this.now.value, granularity) ?? false;
  }

  isFuture(date: Nullish<Moment>): boolean {
    return date?.isAfter(this.today(), 'day') ?? false;
  }

  isTomorrow(date: Nullish<Moment>): boolean {
    return date?.isSame(this.tomorrow(), 'day') ?? false;
  }

  isSoon(date: Nullish<Moment>, timeFrame = 1): boolean {
    return date?.isBetween(this.now.value, this.now.value.clone().add(timeFrame, 'minutes')) ?? false;
  }

  isUrgent(date: Nullish<Moment>, timeFrame = 1): boolean {
    return this.isSoon(date, timeFrame) || this.isPast(date, 'millisecond');
  }

  dateNeedsHelperText(date: Nullish<Moment>): boolean {
    return !(this.isTomorrow(date) || this.isToday(date) || this.isYesterday(date));
  }
}

export default new TimeService();